/*
 * ============================================================
 * NORTA PRODUCT CARD -> HORIZON CART DRAWER
 * ============================================================
 *
 * Intercepta TODOS los formularios:
 *
 *   .norta-card__form
 *
 * Evita la navegación a /cart.
 * Añade el producto mediante AJAX.
 * Dispara el evento nativo de Horizon.
 * Actualiza el carrito.
 * Abre el drawer nativo.
 *
 * Compatible con contenido cargado dinámicamente.
 * ============================================================
 */

import {
  CartLinesUpdateEvent,
  CartErrorEvent
} from '@shopify/events';


const FORM_SELECTOR =
  'form.norta-card__form';

const BUTTON_SELECTOR =
  '.norta-card__button[type="submit"]';


/* ============================================================
   ROUTES
   ============================================================ */

function getCartAddUrl() {
  return (
    window.Theme?.routes?.cart_add_url ||
    window.Shopify?.routes?.root + 'cart/add.js' ||
    '/cart/add.js'
  );
}


function getCartJsonUrl() {
  const cartUrl =
    window.Theme?.routes?.cart_url ||
    '/cart';

  return `${cartUrl}.json`;
}


/* ============================================================
   CART SECTIONS
   ============================================================ */

function appendCartSections(formData) {
  const sectionIds = [];

  document
    .querySelectorAll(
      'cart-items-component[data-section-id]'
    )
    .forEach((component) => {
      const sectionId =
        component.dataset.sectionId;

      if (
        sectionId &&
        !sectionIds.includes(sectionId)
      ) {
        sectionIds.push(sectionId);
      }
    });


  if (sectionIds.length > 0) {
    formData.set(
      'sections',
      sectionIds.join(',')
    );
  }
}


/* ============================================================
   BUTTON STATE
   ============================================================ */

function setButtonLoading(button) {
  if (!button) return;

  button.disabled = true;
  button.setAttribute(
    'aria-busy',
    'true'
  );

  button.dataset.nortaOriginalHtml =
    button.innerHTML;

  const text =
    button.querySelector(
      'span:last-child'
    );

  if (text) {
    text.textContent =
      'Añadiendo...';
  }
}


function setButtonSuccess(button) {
  if (!button) return;

  const text =
    button.querySelector(
      'span:last-child'
    );

  if (text) {
    text.textContent =
      'Añadido';
  }


  window.setTimeout(
    () => {
      restoreButton(button);
    },
    1200
  );
}


function restoreButton(button) {
  if (!button) return;

  if (
    button.dataset.nortaOriginalHtml
  ) {
    button.innerHTML =
      button.dataset.nortaOriginalHtml;
  }

  button.disabled = false;

  button.removeAttribute(
    'aria-busy'
  );

  delete button.dataset
    .nortaOriginalHtml;
}


/* ============================================================
   OPEN HORIZON DRAWER
   ============================================================ */

async function openCartDrawer() {
  const drawer =
    document.querySelector(
      'theme-drawer#cart-drawer'
    );


  if (!drawer) {
    return;
  }


  try {
    await customElements.whenDefined(
      'theme-drawer'
    );
  } catch (error) {
    // Continue with fallback.
  }


  if (
    typeof drawer.open === 'function'
  ) {
    drawer.open();

    return;
  }


  /*
   * Fallback por si el Web Component aún
   * no terminó de inicializar.
   */

  drawer.setAttribute(
    'open',
    ''
  );


  const dialog =
    drawer.querySelector(
      'dialog'
    );


  if (
    dialog &&
    typeof dialog.showModal ===
      'function' &&
    !dialog.open
  ) {
    try {
      dialog.showModal();
    } catch (error) {
      // Drawer native component will handle it.
    }
  }
}


/* ============================================================
   FETCH CURRENT CART
   ============================================================ */

async function fetchCurrentCart() {
  const response =
    await fetch(
      getCartJsonUrl(),
      {
        method: 'GET',

        headers: {
          Accept:
            'application/json'
        },

        credentials:
          'same-origin'
      }
    );


  if (!response.ok) {
    throw new Error(
      `No se pudo actualizar el carrito (${response.status})`
    );
  }


  return response.json();
}


/* ============================================================
   ADD PRODUCT
   ============================================================ */

async function addNortaProduct(
  form,
  submitEvent
) {
  const button =
    form.querySelector(
      BUTTON_SELECTOR
    );


  if (
    button?.disabled
  ) {
    return;
  }


  const formData =
    new FormData(form);


  const variantId =
    formData.get('id');


  if (!variantId) {
    console.error(
      '[Norta Cart] Variant ID missing'
    );

    return;
  }


  const quantity =
    Number(
      formData.get('quantity')
    ) || 1;


  appendCartSections(
    formData
  );


  setButtonLoading(
    button
  );


  /*
   * ==========================================================
   * EVENTO NATIVO HORIZON
   * ==========================================================
   *
   * cart-drawer.js escucha este evento.
   *
   * action: 'add'
   *
   * es justamente lo que provoca que
   * auto-open abra el drawer.
   * ==========================================================
   */

  const deferred =
    CartLinesUpdateEvent
      .createPromise();


  const cartEvent =
    new CartLinesUpdateEvent({
      action: 'add',

      context:
        'product',

      lines: [
        {
          merchandiseId:
            String(variantId),

          quantity:
            quantity
        }
      ],

      promise:
        deferred.promise
    });


  /*
   * El evento debe burbujear hasta document,
   * donde Horizon tiene conectado
   * cart-drawer-component.
   */

  form.dispatchEvent(
    cartEvent
  );


  try {

    const response =
      await fetch(
        getCartAddUrl(),
        {
          method:
            'POST',

          headers: {
            Accept:
              'application/json',

            'X-Requested-With':
              'XMLHttpRequest'
          },

          credentials:
            'same-origin',

          body:
            formData
        }
      );


    const result =
      await response.json();


    if (
      !response.ok ||
      result.status
    ) {
      throw new Error(
        result.description ||
        result.message ||
        'No se pudo añadir el producto.'
      );
    }


    /*
     * Horizon necesita el carrito actualizado
     * para refrescar contador, drawer, cantidades, etc.
     */

    const ajaxCart =
      await fetchCurrentCart();


    const cart =
      CartLinesUpdateEvent
        .createCartFromAjaxResponse(
          ajaxCart
        );


    deferred.resolve({
      cart,

      detail: {
        didError:
          false,

        items:
          ajaxCart.items,

        itemCount:
          quantity,

        productId:
          form
            .closest(
              '[data-product-id]'
            )
            ?.dataset
            .productId,

        sections:
          result.sections,

        source:
          'norta-product-card',

        sourceId:
          form.id ||
          'norta-card'
      }
    });


    setButtonSuccess(
      button
    );


    /*
     * Horizon debe abrirlo por el evento.
     *
     * Además lo abrimos directamente como
     * segunda capa de seguridad.
     */

    window.requestAnimationFrame(
      () => {
        openCartDrawer();
      }
    );

  } catch (error) {

    console.error(
      '[Norta Cart]',
      error
    );


    deferred.reject(
      error
    );


    try {
      form.dispatchEvent(
        new CartErrorEvent({
          error:
            error.message ||
            'No se pudo añadir al carrito',

          code:
            'NORTA_ADD_ERROR'
        })
      );
    } catch (eventError) {
      // Event fallback not required.
    }


    restoreButton(
      button
    );
  }
}


/* ============================================================
   GLOBAL SUBMIT INTERCEPTOR
   ============================================================ */

document.addEventListener(
  'submit',
  (event) => {

    const form =
      event.target;


    if (
      !(form instanceof HTMLFormElement)
    ) {
      return;
    }


    if (
      !form.matches(
        FORM_SELECTOR
      )
    ) {
      return;
    }


    /*
     * CRÍTICO:
     *
     * evitamos por completo que Shopify haga
     * el submit tradicional hacia /cart.
     */

    event.preventDefault();

    event.stopPropagation();

    event.stopImmediatePropagation();


    addNortaProduct(
      form,
      event
    );

  },
  true
);


/* ============================================================
   CLICK SAFETY
   ============================================================ */

/*
 * Algunos componentes pueden intentar manipular
 * el formulario antes del submit.
 *
 * Esta parte garantiza que el botón de Norta
 * jamás funcione como navegación hacia /cart.
 */

document.addEventListener(
  'click',
  (event) => {

    const target =
      event.target;


    if (
      !(target instanceof Element)
    ) {
      return;
    }


    const button =
      target.closest(
        '.norta-card__button[type="submit"]'
      );


    if (!button) {
      return;
    }


    const form =
      button.closest(
        FORM_SELECTOR
      );


    if (!form) {
      return;
    }


    /*
     * El submit normal se deja ocurrir,
     * pero nuestro listener CAPTURE de arriba
     * lo interceptará antes de cualquier redirect.
     */

  },
  true
);